// imports section Start
import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import { useStyles } from "../../../assets/styles/Theme";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import MenuIcon from "@mui/icons-material/Menu";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  IconButton,
  Stack,
  SnackbarContent,
  Divider,
} from "@mui/material";
import { formatDate, formatTime } from "../../../utils/common/DateFormat";
import GuestFooter from "../../guest/GuestFooter";
import { currencyFormat } from "../../../utils/common/CurrencyFormat";
import { useNavigate } from "react-router-dom";
import { clearCashierCart } from "../../../store/ReduxCashierCartReducer";
import { useDispatch } from "react-redux";
import NavbarCashier from "../../common/NavbarCashier";
// imports section End

const PlacedOrderAside = ({
  order,
  details,
  billDetails,
  placedorderItem,
  onViewOrder,

  setLoading,
  loading,
}) => {
  // Hooks start
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Hooks end

  // Handlers start
  const handleClearCart = () => {
    dispatch(clearCashierCart()); // Clear cart items using Redux action
  };

  const handleModifyClick = async () => {
    handleClearCart();
    if (loading) return; // Prevent the function from running if it's already loading
    setLoading(true); // Set loading state to true, disabling UI interactions

    try {
      // Await the order details to load first
      await onViewOrder(placedorderItem); // Wait for the details to be fetched
      // await new Promise((resolve) => setTimeout(resolve, 15000));
      // Now navigate after data is loaded
      if (true) {
        navigate("/Cashier", {
          state: {
            // placedOrderDetails: details.items,
            // billDetails: billDetails.items[0],
            ordercode: order.ORDERCODE,
          },
        });
      } else {
        console.error("Order or Bill Details are missing");
      }
    } catch (error) {
      console.error("Error loading data: ", error);
    } finally {
      setLoading(false); // Reset loading state
    }
  };

  if (!order) {
    return (
      <Grid
        container
        // direction="column"
        sx={styles.aside_content_2}
        // position="relative"
      >
        <Grid
          item
          xs={12}
          sx={{
            borderBottom: `1px solid ${theme.palette.stroke.main}`,
            paddingBottom: "8px",
          }}
        >
          <NavbarCashier />
        </Grid>
        <Grid item xs={12}>
          <SnackbarContent
            message="*Please Select an Order to View Details."
            sx={{
              backgroundColor: theme.palette.system.warning, // Change background color here
              color: "white", // Change text color here
              fontWeight: 500,
              borderRadius: "4px",
              padding: "10px",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            }}
          />

          <GuestFooter />
        </Grid>
      </Grid>
    );
  }

  const { ENUSER, AMOUNT, INVNO, PREFCURRENCY, STSTXT } = order;
  // Fallback values for destructuring
  const {
    ADVANCEAMOUNT = 0,
    BALANCEAMOUNT = 0,
    BILLAMOUNT = 0,
    DISCOUNT = 0,
    GROSSAMOUNT = 0,
    TOTTAXAMOUNT = 0,
    TOTCHARGEAMOUNT = 0,
    PAIDAMOUNT = 0,
  } = billDetails?.items?.[0] || {};

  const formattedDate = formatDate(order.ORDERENDATE);
  const formattedTime = formatTime(order.ORDERTIME);

  const formattedAmount = currencyFormat(AMOUNT);

  const placedOrderDetails = [
    {
      bill: [
        { label: "Gross", value: currencyFormat(GROSSAMOUNT) },
        { label: "Tax", value: currencyFormat(TOTTAXAMOUNT) },
        {
          label: "Other Charges",
          value: currencyFormat(TOTCHARGEAMOUNT),
        },
        {
          label: "Total Paid",
          value: currencyFormat(ADVANCEAMOUNT),
        },
        {
          label: "Nett Amount",
          value: currencyFormat(BILLAMOUNT),
          highlight: true,
        },
        {
          label: "Due Balance",
          value: currencyFormat(BILLAMOUNT - ADVANCEAMOUNT),
          // value: currencyFormat(BALANCEAMOUNT, PREFCURRENCY),
        },
      ],
    },
  ];
  // Handlers end

  return (
    <Grid
      container
      direction="column"
      sx={styles.aside_content}
      position="relative"
    >
      <Grid
        container
        item
        sx={{ ...styles.aside_content_header, flexShrink: 0 }}
      >
        <Grid item xs={6} sx={styles.left_align}>
          <Typography
            sx={{
              ...theme.typography.h2,
              color: theme.palette.text.primary,
            }}
          >
            Order #{INVNO}
          </Typography>
        </Grid>
        <Grid item xs={6} gap={1} sx={styles.right_align}>
          <NavbarCashier />
        </Grid>
      </Grid>

      <Box pt={2} sx={{ overflow: "auto", flex: "1 1 auto" }}>
        <Stack spacing={3}>
          {placedOrderDetails.map((orderDetail, index) => (
            <React.Fragment key={index}>
              {/* details start */}
              <Box>
                <Grid
                  container
                  display={"flex"}
                  justifyContent={"space-between"}
                >
                  <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                    <Typography
                      sx={{
                        ...styles.cs_summary_price,
                        ...styles.left_align,
                        fontSize: "14px",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <AccessTimeFilledIcon
                        sx={{ color: theme.palette.primary.main }}
                      />
                      {formattedTime}
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                    <Typography
                      sx={{
                        ...styles.cs_summary_price,
                        ...styles.left_align,
                        fontSize: "14px",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <CalendarMonthIcon
                        sx={{ color: theme.palette.primary.main }}
                      />
                      {formattedDate}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Grid container>
                  <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                    <Typography
                      sx={{
                        ...styles.cs_summary_price,
                        ...styles.left_align,
                        fontSize: "14px",
                        fontWeight: 500,
                        color: theme.palette.input_field.text,
                      }}
                    >
                      Order Id
                    </Typography>
                    <Typography
                      sx={{
                        ...styles.cs_summary_price,
                        ...styles.left_align,
                      }}
                    >
                      {INVNO}
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                    <Typography
                      sx={{
                        ...styles.cs_summary_price,
                        ...styles.left_align,
                        fontSize: "14px",
                        fontWeight: 500,
                        color: theme.palette.input_field.text,
                      }}
                    >
                      Status
                    </Typography>
                    <Typography
                      sx={{
                        ...styles.cs_summary_price,
                        ...styles.left_align,
                      }}
                    >
                      {STSTXT}
                    </Typography>
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} lg={4} xl={4}>
                    <Typography
                      sx={{
                        ...styles.cs_summary_price,
                        ...styles.left_align,
                        fontSize: "14px",
                        fontWeight: 500,
                        color: theme.palette.input_field.text,
                      }}
                    >
                      Name
                    </Typography>
                    <Typography
                      sx={{
                        ...styles.cs_summary_price,
                        ...styles.left_align,
                      }}
                    >
                      {ENUSER}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* details end */}
              {/* cart content start */}
              {details && details.items && details.items.length > 0 ? (
                <Box>
                  <Grid sx={styles.cs_summary_bill}>
                    <Grid item xs={6} md={6} sx={styles.left_align}>
                      <Typography
                        sx={{
                          color: theme.palette.text.primary,
                          fontSize: theme.typography.body1,
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <ShoppingCartCheckoutIcon
                          sx={{
                            color: theme.palette.primary.main,
                          }}
                        />
                        Cart Content
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={6} sx={styles.right_align}>
                      <Typography
                        sx={{
                          color: theme.palette.text.primary,
                          fontSize: theme.typography.body1,
                          fontWeight: 600,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        Sub Total (LKR)
                      </Typography>
                    </Grid>
                  </Grid>
                  <Box
                    sx={{
                      maxHeight: "35vh",
                      overflow: "auto",
                      boxShadow: 2,
                      px: 1,
                      mt: 1,
                      background: theme.palette.background.fill,
                      borderRadius: 1,
                    }}
                  >
                    {details.items.map((item, index) => (
                      <Grid sx={styles.po_summary_bill} key={index}>
                        <Grid
                          item
                          xs={6}
                          md={6}
                          flexDirection={"column"}
                          sx={styles.left_align}
                        >
                          <Typography sx={styles.cs_summary_description}>
                            {item.MENUNAME}
                          </Typography>

                          <Typography sx={styles.cs_summary_description}>
                            {currencyFormat(item.RATE, PREFCURRENCY)} *{" "}
                            {item.QTY}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={6} sx={styles.right_align}>
                          <Typography sx={styles.cs_summary_price}>
                            {currencyFormat(item.AMOUNT)}
                          </Typography>
                        </Grid>
                      </Grid>
                    ))}
                  </Box>
                  {/* 
                  <Grid gap={2} sx={styles.ks_card_content}>
                    <Button
                      sx={styles.btn_secondary}
                      size="medium"
                      variant="outlined"
                    >
                      View More
                    </Button>
                  </Grid> */}
                </Box>
              ) : (
                <Typography>No details available</Typography>
              )}
              {/* cart content end */}
              {/* <Divider /> */}
              {/* Bill Items list start */}
              <Box
                position="absolute"
                bottom={0}
                width="100%"
                right={0}
                sx={{ px: 2, pb: 2 }}
              >
                {orderDetail.bill.map((billItem, i) => (
                  <Grid sx={styles.cs_summary_bill} key={i}>
                    <Grid item xs={6} md={6} sx={styles.left_align}>
                      <Typography
                        sx={{
                          ...styles.cs_summary_description,
                          ...(billItem.highlight && styles.highlight), // Apply highlight if it's "Net Amount"
                        }}
                      >
                        {billItem.label}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={6} sx={styles.right_align}>
                      <Typography
                        sx={{
                          ...styles.cs_summary_price,
                          ...(billItem.highlight && styles.highlight_red), // Apply highlight if it's "Net Amount"
                        }}
                      >
                        {billItem.value}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Button
                        sx={styles.btn_secondary}
                        size="medium"
                        variant="outlined"
                        fullWidth
                        onClick={handleModifyClick}
                      >
                        Modify
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        sx={styles.btn_primary}
                        size="medium"
                        variant="contained"
                        fullWidth
                        onClick={handleModifyClick}
                      >
                        Pay Now
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
              {/* Bill Items list end */}
            </React.Fragment>
          ))}
        </Stack>
      </Box>
    </Grid>
  );
};

export default PlacedOrderAside;
